<template>
  <section class="tg" :class="{ 'is-dark': dark }">
    <div ref="stage" class="tg__stage" @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp"
         @pointerleave="onLeave" @click="onClick" @dblclick="onDouble">
      <canvas ref="canvas" class="tg__canvas" aria-label="Глобус: рейсы в пути"></canvas>
      <div class="tg__zoom" @pointerdown.stop @click.stop @dblclick.stop>
        <button type="button" title="Крупнее" @click="setZoom(zoomLevel * 1.6)">+</button>
        <button type="button" title="Мельче" @click="setZoom(zoomLevel / 1.6)">−</button>
        <button type="button" title="К рейсам" @click="toFocus">◎</button>
      </div>
      <div v-if="tip" class="tg__tip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
        <template v-if="tip.trip">
          <b>Рейс {{ tip.trip.number }}</b>
          <span>{{ tip.trip.from.name }} → {{ tip.trip.to.name }}</span>
          <span v-if="tip.trip.progress !== null">в пути {{ Math.round(tip.trip.progress * 100) }} % по плану</span>
          <span v-if="tip.trip.unload_at">выгрузка {{ formatWhen(tip.trip.unload_at) }}</span>
          <span v-if="tip.trip.responsible">{{ tip.trip.responsible }}</span>
          <em>нажмите — откроется рейс</em>
        </template>
        <template v-else-if="tip.city">
          <b>{{ tip.city.name }}</b>
          <span>отсюда {{ tip.city.out }} · сюда {{ tip.city.in }}</span>
        </template>
      </div>
      <div v-if="!data && loading" class="tg__loading">Загружаем рейсы…</div>
    </div>

    <div class="tg__meta">
      <div class="tg__count">
        <b>{{ data ? data.total : '—' }}</b>
        <span>{{ plural(data ? data.total : 0, 'рейс', 'рейса', 'рейсов') }} в пути</span>
      </div>
      <ul v-if="topRoutes.length" class="tg__routes">
        <li v-for="r in topRoutes" :key="r.key"><i></i>{{ r.label }} <b>{{ r.count }}</b></li>
      </ul>
      <p v-if="data && data.unplaced && data.unplaced.length" class="tg__unplaced"
         :title="data.unplaced.map((u) => u.name + ' — ' + u.count).join('\n')">
        {{ data.total - data.placed }} без точки на карте: {{ data.unplaced.slice(0, 3).map((u) => u.name).join(', ') }}{{ data.unplaced.length > 3 ? '…' : '' }}
      </p>
      <p v-if="error" class="tg__error">Рейсы в пути не загрузились — глобус обновится сам.</p>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { fetchWorkspaceGlobe } from '@/backend/workspaceApi'
import { LAND_DOTS_B64 } from './globe/landDots.mjs'
import {
  arcPoints, centroid, decodeDots, isVisible, makeView, pick, spinSpeed, truckIndex,
} from './globe/globeMath.mjs'

const props = defineProps({
  scope: { type: String, default: 'all' },
  dark: { type: Boolean, default: true },
})

const REFRESH_MS = 2 * 60 * 1000
const ARC_STEPS = 48

const router = useRouter()
const stage = ref(null)
const canvas = ref(null)
const data = ref(null)
const loading = ref(false)
const error = ref(false)
const tip = ref(null)
const zoomLevel = ref(1)

let land = null
let arcs = []
let focus = { lon: 55, lat: 52 }
let focused = false
let viewLon = 20
let viewLat = 38
let raf = null
let last = 0
let timer = null
let resizeObs = null
let size = { w: 0, h: 0, r: 0, dpr: 1 }
let drag = null
let hover = false
let hitTrips = []
let hitCities = []
let fly = null
const reduceMotion = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const topRoutes = computed(() => {
  const counts = new Map()
  for (const t of data.value?.trips || []) {
    const key = `${t.from.name} → ${t.to.name}`
    counts.set(key, (counts.get(key) || 0) + 1)
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4)
    .map(([label, count]) => ({ key: label, label, count }))
})

function plural(n, one, few, many) {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}

function formatWhen(value) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

async function load() {
  loading.value = true
  try {
    const result = await fetchWorkspaceGlobe(props.scope)
    data.value = result
    error.value = false
    arcs = (result.trips || []).map((trip, i) => ({
      trip,
      points: arcPoints(trip.from, trip.to, ARC_STEPS),
      phase: (i * 0.137) % 1,
    }))
    const cities = result.cities || []
    if (cities.length) {
      focus = centroid(cities)
      if (!focused) {
        viewLon = focus.lon - 40
        viewLat = Math.max(20, Math.min(55, focus.lat - 12))
        focused = true
      }
    }
  } catch (e) {
    error.value = true
  } finally {
    loading.value = false
  }
}

function resize() {
  const el = stage.value
  const c = canvas.value
  if (!el || !c) return
  const rect = el.getBoundingClientRect()
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  size = { w: rect.width, h: rect.height, base: Math.min(rect.width, rect.height) * 0.43, dpr }
  c.width = Math.round(rect.width * dpr)
  c.height = Math.round(rect.height * dpr)
  c.style.width = `${rect.width}px`
  c.style.height = `${rect.height}px`
}

function draw(time) {
  const c = canvas.value
  if (!c || !size.w) return
  const ctx = c.getContext('2d')
  const { w, h, dpr } = size
  const r = size.base * zoomLevel.value
  const cx = w / 2
  const cy = h / 2
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)

  // Атмосфера и шар.
  const halo = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 1.35)
  halo.addColorStop(0, 'rgba(242,183,5,0.28)')
  halo.addColorStop(1, 'rgba(242,183,5,0)')
  ctx.fillStyle = halo
  ctx.beginPath(); ctx.arc(cx, cy, r * 1.35, 0, Math.PI * 2); ctx.fill()
  const sphere = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r)
  sphere.addColorStop(0, '#3a4152')
  sphere.addColorStop(0.55, '#232835')
  sphere.addColorStop(1, '#14161c')
  ctx.fillStyle = sphere
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill()
  ctx.strokeStyle = 'rgba(255,224,138,0.35)'
  ctx.lineWidth = 1
  ctx.stroke()

  const view = makeView(viewLon, viewLat)

  // Сетка: экватор и меридианы — тонко.
  ctx.strokeStyle = 'rgba(255,255,255,0.05)'
  for (let lat = -60; lat <= 60; lat += 30) {
    ctx.beginPath()
    let open = false
    for (let lon = -180; lon <= 180; lon += 6) {
      const p = view.project(lon, lat)
      if (p.z <= 0) { open = false; continue }
      const x = cx + p.x * r
      const y = cy - p.y * r
      if (open) ctx.lineTo(x, y); else { ctx.moveTo(x, y); open = true }
    }
    ctx.stroke()
  }

  // Суша — золотые точки, у края гаснут.
  if (land) {
    const { count, lon, sinLat, cosLat } = land
    const { l0, sin0, cos0 } = view
    for (let i = 0; i < count; i += 1) {
      const d = lon[i] - l0
      const cd = Math.cos(d)
      const z = sin0 * sinLat[i] + cos0 * cosLat[i] * cd
      if (z <= 0.02) continue
      const x = cosLat[i] * Math.sin(d)
      const y = cos0 * sinLat[i] - sin0 * cosLat[i] * cd
      ctx.fillStyle = `rgba(214,178,92,${(0.18 + z * 0.62).toFixed(3)})`
      const s = (1.1 + z * 0.9) * Math.min(1.8, Math.sqrt(zoomLevel.value))
      ctx.fillRect(cx + x * r - s / 2, cy - y * r - s / 2, s, s)
    }
  }

  // Дуги рейсов, бегущий свет и машина по плановому прогрессу.
  hitTrips = []
  const t = time / 1000
  for (const arc of arcs) {
    const pts = arc.points.map((p) => {
      const q = view.project(p.lon, p.lat, p.alt)
      return { x: cx + q.x * r, y: cy - q.y * r, v: isVisible(q.z, p.alt) }
    })
    ctx.lineWidth = 1.4
    ctx.strokeStyle = 'rgba(242,183,5,0.38)'
    ctx.beginPath()
    let open = false
    for (const p of pts) {
      if (!p.v) { open = false; continue }
      if (open) ctx.lineTo(p.x, p.y); else { ctx.moveTo(p.x, p.y); open = true }
    }
    ctx.stroke()
    if (!reduceMotion) {
      const head = Math.floor(((t * 0.35 + arc.phase) % 1) * ARC_STEPS)
      ctx.lineWidth = 2.4
      ctx.strokeStyle = 'rgba(255,236,170,0.95)'
      ctx.beginPath()
      open = false
      for (let i = Math.max(0, head - 7); i <= head; i += 1) {
        const p = pts[i]
        if (!p || !p.v) { open = false; continue }
        if (open) ctx.lineTo(p.x, p.y); else { ctx.moveTo(p.x, p.y); open = true }
      }
      ctx.stroke()
    }
    const truck = pts[truckIndex(arc.trip.progress, ARC_STEPS, (t * 0.05 + arc.phase) % 1)]
    if (truck && truck.v) {
      ctx.shadowColor = 'rgba(242,183,5,0.9)'
      ctx.shadowBlur = 10
      ctx.fillStyle = '#fff6d6'
      ctx.beginPath(); ctx.arc(truck.x, truck.y, 3.2, 0, Math.PI * 2); ctx.fill()
      ctx.shadowBlur = 0
      hitTrips.push({ x: truck.x, y: truck.y, trip: arc.trip })
    }
  }

  // Города: пульсирующие точки и подписи главных.
  hitCities = []
  const cities = data.value?.cities || []
  cities.forEach((city, index) => {
    const q = view.project(city.lon, city.lat)
    if (q.z <= 0.05) return
    const x = cx + q.x * r
    const y = cy - q.y * r
    const pulse = reduceMotion ? 0 : (t * 0.8 + index * 0.31) % 1
    ctx.strokeStyle = `rgba(242,183,5,${(0.6 * (1 - pulse)).toFixed(3)})`
    ctx.lineWidth = 1.2
    ctx.beginPath(); ctx.arc(x, y, 3 + pulse * 10, 0, Math.PI * 2); ctx.stroke()
    ctx.fillStyle = '#f2b705'
    ctx.beginPath(); ctx.arc(x, y, 2.6, 0, Math.PI * 2); ctx.fill()
    if (index < (zoomLevel.value > 1.5 ? 14 : 6) && q.z > 0.3) {
      ctx.font = '600 11px Inter, system-ui, sans-serif'
      ctx.fillStyle = `rgba(255,255,255,${(0.35 + q.z * 0.55).toFixed(3)})`
      ctx.fillText(city.name, x + 7, y - 6)
    }
    hitCities.push({ x, y, city })
  })

  // Блик.
  const gloss = ctx.createRadialGradient(cx - r * 0.45, cy - r * 0.55, 0, cx - r * 0.45, cy - r * 0.55, r * 0.9)
  gloss.addColorStop(0, 'rgba(255,255,255,0.10)')
  gloss.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = gloss
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill()
}

function setZoom(value) {
  zoomLevel.value = Math.max(1, Math.min(4, value))
}

// Плавно повернуть к рейсам и приблизить.
function toFocus() {
  fly = { from: { lon: viewLon, lat: viewLat, zoom: zoomLevel.value }, start: 0,
    to: { lon: focus.lon, lat: Math.max(20, Math.min(60, focus.lat - 4)), zoom: 2.4 } }
}

function onDouble() {
  setZoom(zoomLevel.value * 1.6)
}

function stepFly(time) {
  if (!fly) return
  if (!fly.start) fly.start = time
  const p = Math.min(1, (time - fly.start) / 900)
  const e = 1 - (1 - p) ** 3
  let dLon = (fly.to.lon - fly.from.lon) % 360
  if (dLon > 180) dLon -= 360
  if (dLon < -180) dLon += 360
  viewLon = fly.from.lon + dLon * e
  viewLat = fly.from.lat + (fly.to.lat - fly.from.lat) * e
  zoomLevel.value = fly.from.zoom + (fly.to.zoom - fly.from.zoom) * e
  if (p >= 1) fly = null
}

function frame(time) {
  const dt = last ? Math.min(0.1, (time - last) / 1000) : 0
  last = time
  stepFly(time)
  if (!fly && !drag && !hover && !reduceMotion && zoomLevel.value < 1.5) viewLon += spinSpeed(viewLon, focus.lon) * dt
  if (viewLon > 180) viewLon -= 360
  if (viewLon < -180) viewLon += 360
  draw(time)
  raf = window.requestAnimationFrame(frame)
}

function start() {
  if (raf) return
  last = 0
  raf = window.requestAnimationFrame(frame)
}

function stop() {
  if (raf) window.cancelAnimationFrame(raf)
  raf = null
}

function localPoint(event) {
  const rect = stage.value.getBoundingClientRect()
  return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function onDown(event) {
  fly = null
  const p = localPoint(event)
  drag = { x: p.x, y: p.y, lon: viewLon, lat: viewLat, moved: false }
}

function onMove(event) {
  const p = localPoint(event)
  hover = true
  if (drag) {
    const dx = p.x - drag.x
    const dy = p.y - drag.y
    if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true
    const radius = size.base * zoomLevel.value
    viewLon = drag.lon - (dx / radius) * 57
    viewLat = Math.max(-60, Math.min(75, drag.lat + (dy / radius) * 57))
    tip.value = null
    return
  }
  const trip = pick(hitTrips, p.x, p.y, 12)
  const city = trip ? null : pick(hitCities, p.x, p.y, 10)
  tip.value = trip ? { x: p.x, y: p.y, trip: trip.trip } : city ? { x: p.x, y: p.y, city: city.city } : null
  stage.value.style.cursor = trip ? 'pointer' : drag ? 'grabbing' : 'grab'
}

function onUp() {
  window.setTimeout(() => { drag = null }, 0)
}

function onLeave() {
  hover = false
  drag = null
  tip.value = null
}

function onClick(event) {
  if (drag && drag.moved) return
  const p = localPoint(event)
  const trip = pick(hitTrips, p.x, p.y, 12)
  if (trip) router.push(`/trips/${trip.trip.id}`)
}

function onVisibility() {
  if (document.visibilityState === 'visible') start(); else stop()
}

watch(() => props.scope, () => load())

onMounted(() => {
  land = decodeDots(LAND_DOTS_B64)
  resize()
  resizeObs = new ResizeObserver(() => resize())
  resizeObs.observe(stage.value)
  load()
  start()
  document.addEventListener('visibilitychange', onVisibility)
  timer = window.setInterval(() => { if (document.visibilityState === 'visible') load() }, REFRESH_MS)
})

onBeforeUnmount(() => {
  stop()
  if (resizeObs) resizeObs.disconnect()
  if (timer) window.clearInterval(timer)
  document.removeEventListener('visibilitychange', onVisibility)
})
</script>

<style scoped>
.tg { display: grid; grid-template-columns: minmax(0, 1fr); gap: 10px; color: #fff; }
.tg__stage { position: relative; height: 340px; cursor: grab; user-select: none; touch-action: none;
  animation: tg-in 1.2s cubic-bezier(.2, .8, .2, 1) backwards; }
.tg__canvas { display: block; width: 100%; height: 100%; }
.tg__zoom { position: absolute; right: 6px; bottom: 6px; z-index: 2; display: flex; flex-direction: column; gap: 6px; }
.tg__zoom button { width: 30px; height: 30px; border-radius: 10px; border: 1px solid rgba(255, 255, 255, .16); cursor: pointer;
  color: #ffe08a; background: rgba(23, 25, 31, .7); font: 600 16px/1 system-ui, sans-serif; backdrop-filter: blur(6px);
  transition: background .2s, transform .2s, border-color .2s; }
.tg__zoom button:hover { background: rgba(242, 183, 5, .18); border-color: rgba(242, 183, 5, .6); transform: translateY(-1px); }
.tg__tip { position: absolute; transform: translate(14px, -50%); pointer-events: none; z-index: 2; min-width: 190px;
  display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border-radius: 12px; font-size: 12.5px;
  color: #e5e7eb; background: rgba(23, 25, 31, .92); border: 1px solid rgba(242, 183, 5, .45);
  box-shadow: 0 14px 30px -12px rgba(0, 0, 0, .6); backdrop-filter: blur(6px); }
.tg__tip b { color: #ffe08a; font-size: 13.5px; }
.tg__tip em { font-style: normal; color: #9aa0ad; font-size: 11.5px; margin-top: 2px; }
.tg__loading { position: absolute; inset: 0; display: grid; place-items: center; color: #c9ccd4; font-size: 13px; }
.tg__meta { display: flex; flex-direction: column; gap: 8px; }
.tg__count { display: flex; align-items: baseline; gap: 8px; }
.tg__count b { font-size: 30px; line-height: 1; color: #ffe08a; font-variant-numeric: tabular-nums; }
.tg__count span { color: #c9ccd4; font-size: 13px; }
.tg__routes { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.tg__routes li { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 12px;
  color: #d6d8de; background: rgba(255, 255, 255, .06); border: 1px solid rgba(255, 255, 255, .1); }
.tg__routes li i { width: 6px; height: 6px; border-radius: 50%; background: #f2b705; box-shadow: 0 0 6px #f2b705; }
.tg__routes li b { color: #fff; }
.tg__unplaced { margin: 0; font-size: 11.5px; color: #9aa0ad; cursor: help; }
.tg__error { margin: 0; font-size: 12px; color: #ffb4a8; }
@keyframes tg-in { from { opacity: 0; transform: scale(.85) rotate(-8deg); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .tg__stage { animation: none; } }
</style>
